import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Copy, Download, Link, Filter, Eye, EyeOff } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import type { GalleryAllImages } from '@/features/gallery/types'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

interface GalleryImageUrlCopierProps {
  images: GalleryAllImages[];
  galleryTitle: string;
}

type FilterType = 'all' | 'gallery_photo' | 'job_image' | 'approved' | 'pending';

/**
 * Component that allows users to copy URLs of gallery images in various formats
 * Supports filtering by image type and approval status
 */
export function GalleryImageUrlCopier({ images, galleryTitle }: GalleryImageUrlCopierProps) {
  const [filter, setFilter] = useState<FilterType>('all');
  const [showUrls, setShowUrls] = useState(false);
  const { toast } = useToast();

  // Filter images based on selected filter
  const filteredImages = useMemo(() => {
    return images.filter(image => {
      switch (filter) {
        case 'gallery_photo':
          return image.image_type === 'gallery_photo';
        case 'job_image':
          return image.image_type === 'job_image';
        case 'approved':
          return image.review_status === 'approved';
        case 'pending':
          return image.review_status === 'pending';
        case 'all':
        default:
          return true;
      }
    });
  }, [images, filter]);

  // Generate different URL formats
  const urlFormats = useMemo(() => {
    const urls = filteredImages
      .map(img => img.image_url)
      .filter((url): url is string => typeof url === 'string' && url.length > 0)
    
    return {
      simple: urls.join('\n'),
      markdown: urls.map((url, index) => `![${galleryTitle} Image ${index + 1}](${url})`).join('\n'),
      html: urls.map((url, index) => `<img src="${url}" alt="${galleryTitle} Image ${index + 1}" />`).join('\n'),
      json: JSON.stringify(urls, null, 2),
      csv: `URL,Type,Status,Caption\n${filteredImages.map(img => 
        `"${img.image_url || ''}","${img.image_type || ''}","${img.review_status || ''}","${(img.caption || '').replace(/"/g, '""')}"`
      ).join('\n')}`
    };
  }, [filteredImages, galleryTitle]);

  const copyToClipboard = async (format: keyof typeof urlFormats) => {
    try {
      await navigator.clipboard.writeText(urlFormats[format]);
      toast({
        title: 'URLs Copied!',
        message: `${filteredImages.length} image URLs copied in ${format} format`,
        type: 'success'
      });
    } catch (error) {
      toast({
        title: 'Copy Failed',
        message: 'Unable to copy URLs to clipboard',
        type: 'error'
      });
    }
  };

  const downloadAsFile = (format: keyof typeof urlFormats) => {
    const content = urlFormats[format];
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${galleryTitle.replace(/[^a-z0-9]/gi, '_')}_urls.${format === 'json' ? 'json' : format === 'csv' ? 'csv' : 'txt'}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getFilterLabel = (filterType: FilterType) => {
    switch (filterType) {
      case 'gallery_photo':
        return 'Direct Photos';
      case 'job_image':
        return 'Job Images';
      case 'approved':
        return 'Approved Only';
      case 'pending':
        return 'Pending Only';
      case 'all':
      default:
        return 'All Images';
    }
  };

  const getFilterCounts = () => {
    return {
      all: images.length,
      gallery_photo: images.filter(img => img.image_type === 'gallery_photo').length,
      job_image: images.filter(img => img.image_type === 'job_image').length,
      approved: images.filter(img => img.review_status === 'approved').length,
      pending: images.filter(img => img.review_status === 'pending').length,
    };
  };

  const counts = getFilterCounts();

  if (images.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link className="h-5 w-5" />
            Image URLs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No images available to export URLs</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Link className="h-5 w-5" />
          Image URLs
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filter and Summary */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="h-4 w-4 mr-2" />
                  {getFilterLabel(filter)} ({filteredImages.length})
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setFilter('all')}>
                  All Images ({counts.all})
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setFilter('gallery_photo')}>
                  Direct Photos ({counts.gallery_photo})
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setFilter('job_image')}>
                  Job Images ({counts.job_image})
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setFilter('approved')}>
                  Approved Only ({counts.approved})
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setFilter('pending')}>
                  Pending Only ({counts.pending})
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Badge variant="secondary">
              {filteredImages.length} image{filteredImages.length !== 1 ? 's' : ''}
            </Badge>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowUrls(!showUrls)}
          >
            {showUrls ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
            {showUrls ? 'Hide URLs' : 'Preview URLs'}
          </Button>
        </div>

        {/* URL Preview */}
        {showUrls && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">URL Preview:</h4>
            <Textarea
              value={urlFormats.simple}
              readOnly
              className="text-xs font-mono h-32 resize-none"
              placeholder="No URLs to show"
            />
          </div>
        )}

        {/* Copy/Download Actions */}
        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-medium mb-2">Copy Format:</h4>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard('simple')}
              >
                <Copy className="h-4 w-4 mr-1" />
                Simple List
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard('markdown')}
              >
                <Copy className="h-4 w-4 mr-1" />
                Markdown
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard('html')}
              >
                <Copy className="h-4 w-4 mr-1" />
                HTML
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard('json')}
              >
                <Copy className="h-4 w-4 mr-1" />
                JSON
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard('csv')}
              >
                <Copy className="h-4 w-4 mr-1" />
                CSV
              </Button>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium mb-2">Download File:</h4>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => downloadAsFile('simple')}
              >
                <Download className="h-4 w-4 mr-1" />
                Text File
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => downloadAsFile('json')}
              >
                <Download className="h-4 w-4 mr-1" />
                JSON File
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => downloadAsFile('csv')}
              >
                <Download className="h-4 w-4 mr-1" />
                CSV File
              </Button>
            </div>
          </div>
        </div>

        {/* Summary Info */}
        <div className="border-t pt-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-muted-foreground">
            <div>Photos: {filteredImages.filter(img => img.image_type === 'gallery_photo').length}</div>
            <div>Job Images: {filteredImages.filter(img => img.image_type === 'job_image').length}</div>
            <div>Approved: {filteredImages.filter(img => img.review_status === 'approved').length}</div>
            <div>Pending: {filteredImages.filter(img => img.review_status === 'pending').length}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
